import type { Key } from "react-aria-components";

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { toast } from "@/components/toast/toast-queue";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { useLinkSocialPages } from "../hooks/useLinkSocialPages";
import { useSocialAccounts } from "../hooks/useSocialAccounts";
import { useSocialPages } from "../hooks/useSocialPages";

export function LinkPagesModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const projectId = useGetCurrentProjectId();
  const { data: brands = [] } = useGetBrandsByProjectId(projectId);
  const { data, isLoading } = useSocialAccounts();
  const { data: linkedPages } = useSocialPages();
  const linkPages = useLinkSocialPages();
  const [brandIdByPageId, setBrandIdByPageId] = useState<
    Record<string, number>
  >({});

  const pages = data?.pages ?? [];
  const selectedLinks = Object.entries(brandIdByPageId).map(
    ([pageId, brandId]) => ({ brandId, pageId }),
  );

  const linkedBrandIds = new Set(
    linkedPages?.pages.map((page) => page.brand.id),
  );

  function availableBrandsFor(pageId: string) {
    const brandIdsTakenByOtherPages = new Set(
      Object.entries(brandIdByPageId)
        .filter(([otherPageId]) => otherPageId !== pageId)
        .map(([, brandId]) => brandId),
    );

    return brands.filter(
      (brand) =>
        !linkedBrandIds.has(brand.id) &&
        !brandIdsTakenByOtherPages.has(brand.id),
    );
  }

  function selectBrand(pageId: string, brandId: Key | null) {
    setBrandIdByPageId(({ [pageId]: _, ...rest }) =>
      brandId == null ? rest : { ...rest, [pageId]: Number(brandId) },
    );
  }

  function close() {
    setBrandIdByPageId({});
    onClose();
  }

  function save() {
    linkPages.mutate(
      { pages: selectedLinks },
      {
        onError: () => {
          toast.add(
            { title: "Could not link your pages", type: "error" },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          toast.add(
            { title: "Pages linked to brands", type: "success" },
            { timeout: 5000 },
          );
          close();
        },
      },
    );
  }

  return (
    <Modal classNames={{ modal: "w-[640px]" }} isOpen={isOpen}>
      <Dialog>
        <DialogHeader>Link your pages to brands</DialogHeader>
        <DialogCloseButton onPress={close} />
        <DialogBody className="flex flex-col gap-md">
          {isLoading && (
            <Loader2 className="size-6 animate-spin text-text-brand-secondary" />
          )}
          {!isLoading && pages.length === 0 && (
            <p className="text-text-tertiary">
              No Facebook Pages found for this Meta account.
            </p>
          )}
          {pages.map((page) => (
            <div
              className="flex items-center gap-lg rounded-md border border-border-secondary p-lg"
              key={page.id}
            >
              {page.pictureUrl && (
                <img
                  alt=""
                  className="size-10 rounded-full"
                  src={page.pictureUrl}
                />
              )}
              <div className="flex flex-1 flex-col">
                <span className="font-semibold text-text-primary">
                  {page.name}
                </span>
                <span className="text-sm text-text-tertiary">
                  {page.instagram
                    ? `Instagram: @${page.instagram.username ?? page.instagram.id}`
                    : "No Instagram account linked"}
                  {!page.canPublish && " · You can't publish to this Page"}
                </span>
              </div>
              <Select
                aria-label={`Brand for ${page.name}`}
                className="w-[200px]"
                isDisabled={!page.canPublish}
                onSelectionChange={(brandId) => selectBrand(page.id, brandId)}
                placeholder="Select brand"
                selectedKey={brandIdByPageId[page.id] ?? null}
              >
                <SelectButton />
                <SelectPopover>
                  <SelectListBox items={availableBrandsFor(page.id)}>
                    {(brand) => (
                      <SelectListItem id={brand.id}>
                        {brand.name}
                      </SelectListItem>
                    )}
                  </SelectListBox>
                </SelectPopover>
              </Select>
            </div>
          ))}
        </DialogBody>
        <DialogFooter className="flex gap-lg">
          <Button color="gray" onPress={close} variant="secondary">
            Skip
          </Button>
          <Button
            isDisabled={selectedLinks.length === 0}
            isLoading={linkPages.isPending}
            onPress={save}
            variant="primary"
          >
            Save
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
